import { RefObject } from 'preact';
import { createPortal } from 'preact/compat';
import { useEffect, useState } from 'preact/hooks';

import { useIsTourSpotlight } from '../hooks/useIsTourSpotlight';

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TourGlowRingProps {
  /** The tour target this ring belongs to; it shows only while that target is spotlighted. */
  target: string;
  /** Element the ring wraps. */
  anchorRef: RefObject<HTMLElement>;
  /**
   * What to measure. `element` (default) rings the anchor's own box — right for a panel. `buttons`
   * rings the tight union of the `<button>`s inside it, so a row of controls whose layout margins
   * would otherwise inflate the gap is hugged closely (the clip + snapshot pair).
   */
  fit?: 'element' | 'buttons';
  /** Gap between the measured target and the ring. */
  padding?: number;
  /** Corner radius; the default is a full pill. */
  radius?: number;
}

/** Bounds to ring, in viewport coordinates. `buttons` uses border-boxes, so margins don't inflate it. */
const measureAnchor = (el: HTMLElement, fit: 'element' | 'buttons'): Rect => {
  const buttons = fit === 'buttons' ? el.querySelectorAll('button') : null;
  const targets: Element[] = buttons?.length ? Array.from(buttons) : [el];
  const rects = targets.map((t) => t.getBoundingClientRect());
  const left = Math.min(...rects.map((r) => r.left));
  const top = Math.min(...rects.map((r) => r.top));
  const right = Math.max(...rects.map((r) => r.right));
  const bottom = Math.max(...rects.map((r) => r.bottom));
  return { top, left, width: right - left, height: bottom - top };
};

/**
 * A glow ring around `anchorRef`, rendered `fixed` and portaled to <body>.
 *
 * A ring drawn on the element itself is clipped by the first ancestor with `overflow: hidden` —
 * the stream control bar and video wrapper both clip — which lops off the sides of the ring and
 * its soft halo. Measuring the target and painting a fixed overlay outside that clip is the way
 * around it, and it lets the gap be an exact number of pixels rather than a battle with the
 * buttons' own margins.
 *
 * Shown only while the tour spotlights `target`; it re-measures on scroll and resize so it tracks
 * the buttons if the layout shifts underneath it.
 */
export const TourGlowRing = ({ target, anchorRef, fit = 'element', padding = 6, radius = 9999 }: TourGlowRingProps) => {
  const active = useIsTourSpotlight(target);
  const [rect, setRect] = useState<Rect | null>(null);

  useEffect(() => {
    if (!active) {
      setRect(null);
      return undefined;
    }
    const measure = () => setRect(anchorRef.current ? measureAnchor(anchorRef.current, fit) : null);
    measure();

    const observer = new ResizeObserver(measure);
    if (anchorRef.current) observer.observe(anchorRef.current);
    // `capture` so a scroll in any ancestor scroller counts, not just the window.
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [active, anchorRef, fit]);

  if (!active || !rect || typeof document === 'undefined') return null;

  return createPortal(
    <div
      aria-hidden
      // Above the stream controls (z-2) but below the header (z-19) and the tour banner (z-10) —
      // the app chrome and the guide have to stay on top of a decorative highlight. It is
      // `fixed`, so it out-paints in-flow content at equal z; the banner needs its own z to win.
      className="pointer-events-none fixed z-[5] animate-glow-ring"
      style={{
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
        borderRadius: radius,
      }}
    />,
    document.body,
  );
};

export default TourGlowRing;
